import { editorUrl } from '@/lib/editorBasePath';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Heart, ChevronDown, ChevronRight, Square, Circle, Minus, Shapes, Search, X, Palette, Plus, Trash2, Check } from 'lucide-react';
import { StickerInfosByStyle } from '@/hooks/useBookLoader';
import { YearbookElement } from '@/types/yearbook';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

interface ApiStickerItem {
  id: number;
  object_type: string;
  image_url: string;
  width: number;
  height: number;
  name: string;
  category: string;
  tags: string;
  is_favoris?: boolean;
  format?: 'png' | 'svg' | 'simple_svg';
  default_color?: string;
}

// Each entry in a category can be a single sticker OR an array of color variants
type ApiStickerEntry = ApiStickerItem | ApiStickerItem[];
type ApiStickerResponse = Record<string, ApiStickerEntry[]>;

interface StickersPanelProps {
  onAddSticker: (imageUrl: string, width: number, height: number, opts?: { format?: 'png' | 'svg' | 'simple_svg'; defaultColor?: string }) => void;
  onAddShape: (shapeType: 'rect' | 'circle' | 'line', color?: string) => void;
  stickerInfos: StickerInfosByStyle;
  selectedElement: YearbookElement | null;
  onUpdateElement: (id: string, updates: Partial<YearbookElement>) => void;
  favColors: string[];
  onFavColorsChange: (colors: string[]) => void;
}

const SHAPE_DEFS = [
  { type: 'rect' as const, icon: Square, label: 'Rectangle' },
  { type: 'circle' as const, icon: Circle, label: 'Cercle' },
  { type: 'line' as const, icon: Minus, label: 'Ligne' },
];

/** Flatten an entry to its "primary" sticker (first if array) */
function primarySticker(entry: ApiStickerEntry): ApiStickerItem {
  return Array.isArray(entry) ? entry[0] : entry;
}

/** Get all flat stickers from an entry */
function flatStickers(entry: ApiStickerEntry): ApiStickerItem[] {
  return Array.isArray(entry) ? entry : [entry];
}


const StickersPanel: React.FC<StickersPanelProps> = ({ onAddSticker, onAddShape, stickerInfos, selectedElement, onUpdateElement, favColors, onFavColorsChange }) => {
  const [stickers, setStickers] = useState<ApiStickerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [pendingColor, setPendingColor] = useState('#000000');

  const isShapeSelected = !!(selectedElement && (selectedElement.type === 'shape' || selectedElement.type === 'shape_border'));
  const isSvgStickerSelected = !!(selectedElement && selectedElement.isSticker && selectedElement.stickerFormat === 'svg');
  const isColorEditable = isShapeSelected || isSvgStickerSelected;
  const currentColor = isSvgStickerSelected
    ? (selectedElement?.stickerColor || '#000000')
    : (selectedElement?.textStyle?.color || '#000000');
  const colorSectionLabel = isSvgStickerSelected ? 'Couleur du sticker' : 'Couleur de la forme';

  const handleAddFavColor = () => {
    const color = pendingColor;
    onFavColorsChange([...favColors, color]);
    setColorPickerOpen(false);
    setPendingColor('#000000');
    fetch(editorUrl('/save_favorites_colors'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ new_color: color }),
    }).catch(() => {
      onFavColorsChange(favColors.filter((c) => c !== color));
    });
  };

  const handleRemoveFavColor = (color: string) => {
    onFavColorsChange(favColors.filter((c) => c !== color));
    fetch(editorUrl('/remove_favorites_colors'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ color }),
    }).catch(() => {
      onFavColorsChange([...favColors, color]);
    });
  };

  const handleSetColor = (color: string) => {
    if (!selectedElement) return;
    if (isSvgStickerSelected) {
      onUpdateElement(selectedElement.id, { stickerColor: color });
    } else if (isShapeSelected) {
      onUpdateElement(selectedElement.id, {
        textStyle: { ...selectedElement.textStyle!, color },
      });
    }
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    fetch(editorUrl('/load_stickers'))
      .then((res) => res.json())
      .then((data: ApiStickerResponse) => {
        if (!cancelled) {
          setStickers(data);
          const favSet = new Set<number>();
          for (const entries of Object.values(data)) {
            for (const entry of entries) {
              for (const item of flatStickers(entry)) {
                if (item.is_favoris) favSet.add(item.id);
              }
            }
          }
          setFavorites(favSet);
          const allKeys = Object.keys(data);
          setCollapsedCategories(new Set(allKeys));
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const toggleFavorite = useCallback((e: React.MouseEvent, stickerId: number) => {
    e.stopPropagation();
    const isFav = favorites.has(stickerId);
    const endpoint = isFav ? '/editor/remove_sticker_to_favori' : '/editor/add_sticker_to_favori';

    setFavorites((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(stickerId);
      else next.add(stickerId);
      return next;
    });

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sticker_id: stickerId }),
    }).catch(() => {
      setFavorites((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(stickerId);
        else next.delete(stickerId);
        return next;
      });
    });
  }, [favorites]);

  const toggleCategory = (key: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const sortedKeys = stickers ? Object.keys(stickers).sort((a, b) => Number(a) - Number(b)) : [];

  const searchResults = useMemo(() => {
    if (!stickers || !searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    const results: ApiStickerItem[] = [];
    for (const key of sortedKeys) {
      for (const entry of (stickers[key] || [])) {
        for (const item of flatStickers(entry)) {
          if (item.name.toLowerCase().includes(q) || (item.tags && item.tags.toLowerCase().includes(q))) {
            results.push(item);
          }
        }
      }
    }
    return results;
  }, [stickers, searchQuery, sortedKeys]);

  const renderStickerItem = (item: ApiStickerItem) => (
    <div key={item.id} className="relative group h-[72px]">
      <button
        onClick={() => onAddSticker(item.image_url, item.width, item.height, { format: item.format, defaultColor: item.default_color })}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('application/yearbook-sticker', JSON.stringify({
            url: item.image_url,
            width: item.width,
            height: item.height,
          }));
          e.dataTransfer.effectAllowed = 'copy';
        }}
        className="w-full h-full rounded-md bg-editor-sidebar-hover border border-editor-sidebar-border hover:scale-105 hover:border-editor-sidebar-active transition-all flex items-center justify-center p-1.5 overflow-hidden cursor-grab active:cursor-grabbing"
        title={item.name}
      >
        <img
          src={item.image_url}
          alt={item.name}
          className="max-w-[90%] max-h-[90%] object-contain pointer-events-none"
          loading="lazy"
        />
      </button>
      <button
        onClick={(e) => toggleFavorite(e, item.id)}
        className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/40 hover:bg-black/60 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
        title={favorites.has(item.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
      >
        <Heart
          size={12}
          className={favorites.has(item.id) ? 'text-red-500 fill-red-500' : 'text-white'}
        />
      </button>
    </div>
  );

  const toggleExpanded = useCallback((id: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Render a single entry: if array, show first + inline variants */
  const renderEntry = (entry: ApiStickerEntry, index: number): React.ReactNode => {
    if (!Array.isArray(entry)) {
      return renderStickerItem(entry);
    }
    if (entry.length === 0) return null;
    const first = entry[0];
    const isExpanded = expandedGroups.has(first.id);
    return (
      <React.Fragment key={first.id}>
        <div className="relative group h-[72px]">
          <button
            onClick={() => onAddSticker(first.image_url, first.width, first.height, { format: first.format, defaultColor: first.default_color })}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('application/yearbook-sticker', JSON.stringify({
                url: first.image_url,
                width: first.width,
                height: first.height,
              }));
              e.dataTransfer.effectAllowed = 'copy';
            }}
            className="w-full h-full rounded-md bg-editor-sidebar-hover border border-editor-sidebar-border hover:scale-105 hover:border-editor-sidebar-active transition-all flex items-center justify-center p-1.5 overflow-hidden cursor-grab active:cursor-grabbing"
            title={first.name}
          >
            <img
              src={first.image_url}
              alt={first.name}
              className="max-w-[90%] max-h-[90%] object-contain pointer-events-none"
              loading="lazy"
            />
          </button>
          {entry.length > 1 && !isExpanded && (
            <button
              onClick={(e) => { e.stopPropagation(); toggleExpanded(first.id); }}
              className="absolute bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-editor-sidebar-active/90 text-white text-[8px] font-medium flex items-center gap-0.5 hover:bg-editor-sidebar-active transition-colors whitespace-nowrap"
              title="Voir les variantes de couleurs"
            >
              <Palette size={8} />
              + de couleurs
            </button>
          )}
          <button
            onClick={(e) => toggleFavorite(e, first.id)}
            className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/40 hover:bg-black/60 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
            title={favorites.has(first.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          >
            <Heart
              size={12}
              className={favorites.has(first.id) ? 'text-red-500 fill-red-500' : 'text-white'}
            />
          </button>
        </div>
        {isExpanded && entry.slice(1).map((variant) => renderStickerItem(variant))}
      </React.Fragment>
    );
  };

  /** Get all favorite items across all entries */
  const favItems = useMemo(() => {
    if (!stickers) return [];
    return sortedKeys.flatMap((key) =>
      (stickers[key] || []).flatMap((entry) =>
        flatStickers(entry).filter((item) => favorites.has(item.id))
      )
    );
  }, [stickers, sortedKeys, favorites]);

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-editor-sidebar-foreground text-center flex items-center justify-center gap-1.5">
        <Shapes size={15} />
        Stickers & Formes
      </h3>
      {/* Standard shapes */}
      <div className="space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Formes</p>
        <div className="flex gap-2">
          {SHAPE_DEFS.map(({ type, icon: Icon, label }) => (
            <button
              key={type}
              onClick={() => onAddShape(type)}
              className="flex-1 flex flex-col items-center gap-1 py-2.5 rounded-lg border border-editor-sidebar-border bg-editor-sidebar-hover/50 hover:border-editor-sidebar-active/40 hover:bg-editor-sidebar-hover transition-all"
              title={label}
            >
              <Icon size={20} className="text-editor-sidebar-foreground" />
              <span className="text-[10px] text-muted-foreground">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Color section – visible when a shape or an SVG sticker is selected */}
      {isColorEditable && (
        <div className="space-y-2 border border-editor-sidebar-border rounded-lg p-3 bg-editor-sidebar-hover/30">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{colorSectionLabel}</p>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-md border border-editor-sidebar-border shrink-0" style={{ backgroundColor: currentColor }} />
            <span className="text-xs text-editor-sidebar-foreground font-mono">{currentColor}</span>
          </div>
          <div className="grid grid-cols-6 gap-1.5">
            {favColors.map((color, idx) => (
              <div key={`${color}-${idx}`} className="relative group aspect-square">
                <button
                  onClick={() => handleSetColor(color)}
                  className={`w-full h-full rounded-md border transition-transform hover:scale-110 ${
                    currentColor === color ? 'border-editor-sidebar-active ring-1 ring-editor-sidebar-active' : 'border-editor-sidebar-border'
                  }`}
                  style={{ backgroundColor: color }}
                />
                <button
                  onClick={() => handleRemoveFavColor(color)}
                  className="absolute top-0 right-0 p-0.5 rounded-bl-md rounded-tr-md bg-black/60 hover:bg-black/80 transition-colors opacity-0 group-hover:opacity-100"
                  title="Supprimer cette couleur"
                >
                  <Trash2 size={10} className="text-white" />
                </button>
              </div>
            ))}
            <div className="aspect-square">
              <button
                onClick={() => setColorPickerOpen(true)}
                className="w-full h-full rounded-md border border-dashed border-editor-sidebar-border hover:border-editor-sidebar-active transition-colors flex items-center justify-center"
                title="Ajouter une couleur"
              >
                <Plus size={12} className="text-muted-foreground" />
              </button>
            </div>
          </div>

          <Dialog open={colorPickerOpen} onOpenChange={setColorPickerOpen}>
            <DialogContent className="sm:max-w-sm p-0 gap-0 overflow-hidden">
              <DialogTitle className="px-5 pt-5 pb-0 text-sm font-semibold">Choisir une couleur</DialogTitle>
              <div className="p-5 space-y-4">
                <HexColorPicker color={pendingColor} onChange={setPendingColor} style={{ width: '100%', height: 180 }} />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg border border-border shrink-0 shadow-sm" style={{ backgroundColor: pendingColor }} />
                  <HexColorInput
                    color={pendingColor}
                    onChange={setPendingColor}
                    prefixed
                    className="flex-1 text-sm font-mono bg-muted text-foreground rounded-md px-3 py-2 border border-border uppercase"
                  />
                  <button
                    onClick={handleAddFavColor}
                    className="flex items-center gap-1.5 text-sm bg-primary-gradient text-primary-foreground px-4 py-2 rounded-md hover:opacity-90 transition-opacity shrink-0 font-medium"
                  >
                    <Check size={14} />
                    Ajouter
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="animate-spin text-muted-foreground" size={20} />
        </div>
      )}

      {!loading && (!stickers || sortedKeys.length === 0) && (
        <p className="text-xs text-muted-foreground">Aucun sticker disponible.</p>
      )}

      {!loading && stickers && sortedKeys.length > 0 && (
        <>
          {/* Category filter chips */}
           <div className="flex flex-wrap gap-1.5">
            <span
              className="text-[10px] px-2 py-1 rounded-full border transition-colors bg-editor-sidebar-active text-white border-editor-sidebar-active cursor-default"
            >
              ❤️ Favoris
            </span>
            {sortedKeys.map((key) => {
              const info = stickerInfos[key];
              if (!info) return null;
              const isOpen = !collapsedCategories.has(key);
              return (
                <button
                  key={key}
                  onClick={() => toggleCategory(key)}
                  className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                    isOpen
                      ? 'bg-editor-sidebar-active text-white border-editor-sidebar-active'
                      : 'bg-transparent text-editor-sidebar-foreground border-editor-sidebar-border hover:border-editor-sidebar-active'
                  }`}
                >
                  {info.name}
                </button>
              );
            })}
          </div>

          {/* Search input */}
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un sticker…"
              className="w-full pl-7 pr-7 py-1.5 text-xs rounded-md border border-editor-sidebar-border bg-editor-sidebar-hover/50 text-editor-sidebar-foreground placeholder:text-muted-foreground focus:outline-none focus:border-editor-sidebar-active transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-editor-sidebar-foreground transition-colors"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {/* Search results */}
          {searchResults !== null ? (
            searchResults.length === 0 ? (
              <p className="text-[10px] text-muted-foreground italic">Aucun résultat pour « {searchQuery} ».</p>
            ) : (
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{searchResults.length} résultat{searchResults.length > 1 ? 's' : ''}</p>
                <div className="grid grid-cols-3 gap-2">
                  {searchResults.map((item) => renderStickerItem(item))}
                </div>
              </div>
            )
          ) : (
            <>
          {/* Favoris section – always open */}
          {favItems.length === 0 ? (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground">❤️ Favoris</h4>
              <p className="text-[10px] text-muted-foreground italic">Aucun favori pour le moment.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground">❤️ Favoris</h4>
              <div className="grid grid-cols-3 gap-2">
                {favItems.map((item) => renderStickerItem(item))}
              </div>
            </div>
          )}

          {/* Category sections */}
          {sortedKeys.map((key) => {
            const entries = stickers[key];
            if (!entries || entries.length === 0) return null;
            const info = stickerInfos[key];
            const categoryName = info?.name || key;
            const isCollapsed = collapsedCategories.has(key);

            return (
              <div key={key} className="space-y-2">
                <button
                  onClick={() => toggleCategory(key)}
                  className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-editor-sidebar-foreground transition-colors w-full"
                >
                  {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                  <span className="capitalize">{categoryName}</span>
                </button>
                {!isCollapsed && (
                  <div className="grid grid-cols-3 gap-2">
                    {entries.map((entry, i) => renderEntry(entry, i))}
                  </div>
                )}
              </div>
            );
          })}
            </>
          )}
        </>
      )}

    </div>
  );
};

export default StickersPanel;
